import {
  EmbeddedBuyNowLayout_productizedService$data,
  EmbeddedBuyNowLayout_productizedService$key,
} from '#app/__generated__/relay/EmbeddedBuyNowLayout_productizedService.graphql.js';
import { Logo } from '#app/components/Logo/src/Logo.js';
import { Text } from '#app/components/Primitives/Text.js';
import { ProjectCoverWrapper } from '#app/components/ProjectCover/ProjectCoverWrapper.js';
import { useSafeUserReviews } from '#app/hooks/useSafeUserReviews.js';
import { EmbedBuyNowCTA } from '../components/EmbedBuyNowCTA.js';
import {
  EmbedStructureVariant,
  useEmbedVariant,
} from '../hooks/useEmbedVariant.js';
import { RecommendationStarFullIcon } from '@contra/icons/icons';
import { graphql, useFragment } from '@contra/relay';
import { css, styled } from 'styled-components';

const getContainerVariantStyles = (structure: EmbedStructureVariant) => {
  switch (structure) {
    case 'vertical':
      return css`
        flex-flow: column nowrap;
        gap: 16px;
        padding: 16px;
        border-radius: 10px;
        width: 384px;
      `;
    case 'horizontal':
    default:
      return css`
        flex-flow: row nowrap;
        height: 172px;
        gap: 24px;
        padding: 16px;
        border-radius: 10px;
        min-width: 200px;
      `;
  }
};

type Price = EmbeddedBuyNowLayout_productizedService$data['price'];

const getFriendlyPriceLabel = (price: Price) => {
  const numberFormat = Intl.NumberFormat('en-US');
  const formattedAmount =
    price.amount === null ? null : numberFormat.format(price.amount);

  switch (price.type) {
    case 'CONTACT_FOR_PRICING':
      return 'Contact for Pricing';
    case 'FIXED_PRICE':
      return `$${formattedAmount}`;
    case 'RATE':
      return `$${formattedAmount}/${price.interval?.toLowerCase()}`;
    default:
      return null;
  }
};

const Container = styled.div<{ $structure: EmbedStructureVariant }>`
  ${({ $structure, theme }) => css`
    border: 1px solid ${theme.colorsV2.gray30};
    background-color: ${theme.colorsV2.white};
    display: flex;
    position: relative;

    ${getContainerVariantStyles($structure)}
  `}
`;

const Content = styled.div<{ $structure: EmbedStructureVariant }>`
  ${({ $structure }) => css`
    display: flex;
    flex-flow: column nowrap;
    justify-content: space-between;
    height: 100%;
    width: 100%;
    gap: ${$structure === 'vertical' ? '24px' : '0px'};
  `}
`;

const Rating = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  gap: 4px;
`;

const TitleAndDetails = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: 8px;
`;

const Title = styled.h2`
  ${({ theme }) => css`
    color: ${theme.colorsV2.black};
    font-size: 19px;
    font-weight: 500;
    line-height: 28px;
    -webkit-line-clamp: 2;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    overflow: hidden;
  `}
`;

const Details = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-flow: row nowrap;
    align-items: center;
    gap: 8px;
    color: ${theme.colorsV2.gray80};
  `}
`;

const DetailDivider = styled.div`
  ${({ theme }) => css`
    background-color: ${theme.colorsV2.gray40};
    height: 4px;
    width: 4px;
    border-radius: 50%;
  `}
`;

const ContentFooter = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
  align-items: center;

  & svg {
    height: 28px;
    width: 28px;
  }
`;

const CoverBlock = styled.div<{ $structure: EmbedStructureVariant }>`
  ${({ $structure }) => css`
    ${$structure === 'vertical' &&
    css`
      width: 352px;
      height: 266px;
    `}
    ${$structure === 'horizontal' &&
    css`
      height: 140px;
      width: 186px;
    `}
  flex-shrink: 0;
  `}
`;

const CoverContainer = styled.div<{ width: number }>`
  ${({ width }) => css`
    z-index: 1;
    border-radius: 8px;
    position: absolute;
    padding: 16px;
    inset: 0;
    width: ${width}px;
  `}
`;

type EmbeddedBuyNowLayoutProps = {
  readonly productizedServiceRef?: EmbeddedBuyNowLayout_productizedService$key;
};

export const EmbeddedBuyNowLayout = ({
  productizedServiceRef,
}: EmbeddedBuyNowLayoutProps) => {
  const service = useFragment<EmbeddedBuyNowLayout_productizedService$key>(
    graphql`
      fragment EmbeddedBuyNowLayout_productizedService on ProductizedService {
        title
        price {
          amount
          interval
          type
          timeline
        }
        cover {
          ...ProjectCoverWrapper_portfolioProjectCover
        }
        userProfile {
          ...useSafeUserReviews_userProfile
        }
        ...EmbedBuyNowCTA_productizedService
      }
    `,
    productizedServiceRef,
  );
  const embedVariant = useEmbedVariant();

  const { averageRating, reviews } = useSafeUserReviews({
    userProfileRef: service?.userProfile,
  });

  if (!service) {
    return null;
  }

  return (
    <Container $structure={embedVariant.structure}>
      {service.cover ? (
        <CoverBlock $structure={embedVariant.structure}>
          <CoverContainer
            width={embedVariant.structure === 'vertical' ? 352 : 186}
          >
            <ProjectCoverWrapper
              coverRef={service.cover}
              objectFit="cover"
              width={embedVariant.structure === 'vertical' ? 352 : 186}
            />
          </CoverContainer>
        </CoverBlock>
      ) : null}

      <Content $structure={embedVariant.structure}>
        <TitleAndDetails>
          <Title>{service.title}</Title>
          <Details>
            {reviews.length > 0 ? (
              <>
                <Rating>
                  <RecommendationStarFullIcon size={12} />
                  <Text textStyle="bodySmall">{averageRating}</Text>
                </Rating>
                <DetailDivider />
              </>
            ) : null}
            <Text textStyle="bodySmall">
              {getFriendlyPriceLabel(service.price)}
            </Text>
            {service.price.timeline ? (
              <>
                <DetailDivider />
                <Text textStyle="bodySmall">{service.price.timeline}</Text>
              </>
            ) : null}
          </Details>
        </TitleAndDetails>
        <ContentFooter>
          <EmbedBuyNowCTA productizedServiceRef={service} />

          <Logo
            condensed
            variant="black"
          />
        </ContentFooter>
      </Content>
    </Container>
  );
};
